import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreateRoom } from './CreateRoom';
import { JoinRoom } from './JoinRoom';
import { useSocket } from '@hooks/useSocket';
import { useRoomStore } from '@store/roomStore';
import { useUserStore } from '@store/userStore';
import styles from './RoomManager.module.css';

export const RoomManager: React.FC = () => {
  const [mode, setMode] = useState<'menu' | 'create' | 'join'>('menu');
  const [nameInput, setNameInput] = useState('');
  const [isNameSet, setIsNameSet] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const navigate = useNavigate();
  const { createRoom, joinRoom } = useSocket();
  const setRoomCode = useRoomStore((state) => state.setRoomCode);
  const setPlayerColor = useRoomStore((state) => state.setPlayerColor);
  const { playerName, setPlayerName: storePlayerName, setPlayerId } = useUserStore();
  
  const handleNameSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (nameInput.trim()) {
      storePlayerName(nameInput.trim());
      setIsNameSet(true);
    }
  };
  
  const handleCreateRoom = async (settings: any) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await createRoom(playerName, settings);
      setRoomCode(result.roomCode);
      setPlayerColor(result.color);
      setPlayerId(result.playerId);
      navigate(`/room/${result.roomCode}`);
    } catch (err: any) {
      setError(err.message || 'Failed to create room');
    } finally {
      setLoading(false);
    }
  };
  
  const handleJoinRoom = async (roomCode: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await joinRoom(roomCode, playerName);
      setRoomCode(roomCode);
      setPlayerColor(result.color);
      setPlayerId(result.playerId);
      
      // If there's already an opponent, set them
      if (result.opponent) {
        const setOpponent = useRoomStore.getState().setOpponent;
        setOpponent(result.opponent);
      }
      
      navigate(`/room/${roomCode}`);
    } catch (err: any) {
      setError(err.message || 'Failed to join room');
    } finally {
      setLoading(false);
    }
  };
  
  if (mode === 'create') {
    return (
      <CreateRoom
        onBack={() => setMode('menu')}
        onCreate={handleCreateRoom}
        loading={loading}
        error={error}
      />
    );
  }
  
  if (mode === 'join') {
    return (
      <JoinRoom
        onBack={() => setMode('menu')}
        onJoin={handleJoinRoom}
        loading={loading}
        error={error}
      />
    );
  }
  
  return (
    <div className={styles.container}>
      <div className={styles.menu}>
        <h2 className={styles.title}>Welcome to Online Chess</h2>
        
        {!isNameSet ? (
          <form onSubmit={handleNameSubmit} className={styles.form}>
            <input
              type="text"
              placeholder="Enter your name"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              maxLength={20}
              className={styles.input}
              autoFocus
            />
            <button type="submit" className={styles.button} disabled={!nameInput.trim()}>
              Continue
            </button>
          </form>
        ) : (
          <div className={styles.options}>
            <p className={styles.greeting}>Hello, {playerName}!</p>
            <button
              onClick={() => setMode('create')}
              className={`${styles.button} ${styles.primaryButton}`}
            >
              Create Room
            </button>
            <button
              onClick={() => setMode('join')}
              className={`${styles.button} ${styles.secondaryButton}`}
            >
              Join Room
            </button>
          </div>
        )}
        
        {error && <p className={styles.error}>{error}</p>}
      </div>
    </div>
  );
};